import { createContext, useContext, useState, useEffect } from "react";
import { useLocation } from "wouter";
import posthog from "posthog-js";
import type { StatusOrg } from "@shared/org-status";

export type PapelUsuario = "ADMIN" | "GESTOR" | "ENCARREGADO" | "CAMPO" | "LEITURA";

export interface User {
  id: string;
  /** Opcional desde a 055: o corretor entra pelo telefone. */
  email: string | null;
  telefone?: string | null;
  name: string;
  role: PapelUsuario;
  /** Migração 066 — desativado não entra; a sessão viva dele cai em ≤ 60 s. */
  ativo?: boolean;
  /** Quem administra o RIZOMA (módulo Plataforma). Não é papel de org. */
  plataformaAdmin?: boolean;
  /**
   * Os módulos que ESTE usuário vê, já EFETIVOS (org ∩ usuário). É o que o menu,
   * o seletor e o `ProtectedRoute` leem — `modulesEnabled` abaixo é o da ORG e
   * fica só para a tela Usuários oferecer as opções.
   */
  modulos?: string[];
  /**
   * Qual FUNCIONÁRIO este login é (migração 052). `null` = sem vínculo, e aí o
   * operador vê todas as operações do dia (spec §4.7) — é o que decide se o chip
   * "Só as minhas" existe na tela Hoje.
   *
   * Vem de graça: `/api/auth/me` devolve a linha inteira de `users` menos a
   * senha. Só faltava declarar aqui.
   */
  funcionarioId?: string | null;
  orgId?: string;
  orgName?: string;
  /**
   * PRODUTOR (default de toda org existente) ou COMPRADOR. Decide o CONTEÚDO da tela
   * /mercado — não decide acesso: quem decide acesso é `modulesEnabled` (menu) e o
   * `requireModule`/`requireOrgTipo` do servidor (API).
   */
  orgTipo?: "PRODUTOR" | "COMPRADOR";
  /**
   * CORRETOR | DISTRIBUIDOR — o PERFIL da org compradora (C5a). Viaja junto de
   * `orgTipo` porque responde a pergunta vizinha: `orgTipo` diz de que LADO do
   * balcão a org está, `perfilComprador` diz como ela se APRESENTA.
   *
   * ⚠️ NÃO GATEIA ROTA — decide o que a tela MOSTRA (margem para o corretor,
   * custo por kg para o distribuidor) e quais itens de menu existem ("A receber"
   * é só do corretor, C8b). Todo gate de verdade é `requireOrgTipo` /
   * `requireCorretor` no servidor; o que decide número é o DTO, que carrega o
   * próprio `perfil`.
   */
  perfilComprador?: "CORRETOR" | "DISTRIBUIDOR" | null;
  modulesEnabled?: string[];
  /**
   * O status da ORG (shared/org-status.ts), calculado no servidor pela mesma regra
   * do `trialMiddleware`. Decide só o que a tela MOSTRA — em CORTESIA somem o item
   * "Assinatura" e a parede de trial (`orgVeAssinatura`). Ausente no user antigo
   * do localStorage e no modo suporte: aí tudo fica como era.
   */
  orgStatus?: StatusOrg | null;
  /**
   * Modo SUPORTE ("Entrar como", P2): presente só quando o cookie de suporte
   * prevalece — a sessão é LEITURA na org alvo. `usePode` devolve false para
   * tudo que não é leitura; o `AppLayout` mostra o banner.
   */
  suporte?: { acessoId: string; orgNome: string | null } | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isEncarregado: boolean;
  /** `user.plataformaAdmin` — decide o grupo Plataforma do menu e as telas dele. */
  isPlataforma: boolean;
  /** Os módulos EFETIVOS do usuário (org ∩ usuário). Nome antigo mantido. */
  modulesEnabled: string[];
  isLoading: boolean;
  isBlocked: boolean;
  login: (user: User) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isBlocked, setIsBlocked] = useState(false);
  const [, setLocation] = useLocation();

  useEffect(() => {
    const handleTrialExpired = () => setIsBlocked(true);
    window.addEventListener('rizoma:trial-expired', handleTrialExpired);
    return () => window.removeEventListener('rizoma:trial-expired', handleTrialExpired);
  }, []);

  useEffect(() => {
    // Boot: verificar sessão via /api/auth/me (valida o cookie server-side)
    // Usa o user do localStorage como valor inicial para evitar flash
    const cachedUser = localStorage.getItem("user");
    if (cachedUser) {
      try {
        setUser(JSON.parse(cachedUser));
      } catch {
        localStorage.removeItem("user");
      }
    }

    // Validar com o servidor (cookie pode ter expirado ou sido invalidado)
    fetch("/api/auth/me", { credentials: "include" })
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          setUser(data.user);
          localStorage.setItem("user", JSON.stringify(data.user));
          if (posthog.__loaded) {
            posthog.identify(data.user.id, {
              email: data.user.email,
              name: data.user.name,
              role: data.user.role,
            });
          }
        } else {
          // Cookie inválido ou expirado — limpar estado local
          setUser(null);
          localStorage.removeItem("user");
        }
      })
      .catch(() => {
        // Falha de rede — manter o user do cache (app pode funcionar offline brevemente)
        // Próxima request de API vai retornar 401 e redirecionar para login
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const login = (user: User) => {
    setUser(user);
    localStorage.setItem("user", JSON.stringify(user));
    if (posthog.__loaded) {
      posthog.identify(user.id, {
        email: user.email,
        name: user.name,
        role: user.role,
      });
    }
  };

  const logout = async () => {
    try {
      // Limpar cookie server-side primeiro
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // Se falhar (offline), continua com logout local
    } finally {
      localStorage.removeItem("user");
      setUser(null);
      if (posthog.__loaded) {
        posthog.reset();
      }
      setLocation("/");
    }
  };

  const isAuthenticated = !!user;
  const isAdmin = user?.role === "ADMIN";
  const isEncarregado = user?.role === "ENCARREGADO";
  const isPlataforma = !!user?.plataformaAdmin;
  // `modulos` (efetivo, PR 1) manda; `modulesEnabled` só segura o user gravado no
  // localStorage antes do deploy, até o /api/auth/me do boot trocar por um novo.
  const modulesEnabled = user?.modulos ?? user?.modulesEnabled ?? ["cacho"];

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isAdmin, isEncarregado, isPlataforma, modulesEnabled, isLoading, isBlocked, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
